import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import { Users, Lock } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const ROLE_LABEL = { peer: "Peer", direct_report: "Direct report", cross_functional: "Cross-functional partner" };
const SCORE_LABELS = {
  1: "Developing", 2: "Progressing", 3: "Meeting", 4: "Exceeding", 5: "Role-model",
};

export default function Rate360() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  // taskId → { score, comment }
  const [ratings, setRatings] = useState({});
  const [overall, setOverall] = useState("");
  const [strengths, setStrengths] = useState("");
  const [growth, setGrowth] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);

  useEffect(() => {
    axios.get(`${API}/public/360/rate/${token}`)
      .then((r) => setData(r.data))
      .catch((e) => setError(e?.response?.data?.detail || "This link isn't valid or has expired."))
      .finally(() => setLoading(false));
  }, [token]);

  const tasks = data?.cycle?.tasks || [];
  const ratedCount = useMemo(
    () => Object.values(ratings).filter((r) => r?.score != null || (r?.comment || "").trim()).length,
    [ratings],
  );

  const setScore = (taskId, score) =>
    setRatings((r) => ({ ...r, [taskId]: { ...(r[taskId] || {}), score } }));
  const setComment = (taskId, comment) =>
    setRatings((r) => ({ ...r, [taskId]: { ...(r[taskId] || {}), comment } }));

  const submit = async (e) => {
    e.preventDefault();
    const anyFilled = ratedCount > 0 || overall.trim() || strengths.trim() || growth.trim();
    if (!anyFilled) {
      toast.error("Please rate at least one task or leave a comment before submitting.");
      return;
    }
    setSubmitting(true);
    try {
      await axios.post(`${API}/public/360/rate/${token}`, {
        ratings: tasks.map((t) => ({
          task_id: t.id,
          score: ratings[t.id]?.score ?? null,
          comment: (ratings[t.id]?.comment || "").trim() || null,
        })),
        overall_comment: overall.trim() || null,
        strengths: strengths.trim() || null,
        growth_areas: growth.trim() || null,
      });
      setDone(true);
      toast.success("Thanks — your feedback has been locked in the vault.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit — please try again.");
    } finally {
      setSubmitting(false);
    }
  };

  if (loading) {
    return <div className="max-w-xl mx-auto px-6 py-24 text-center font-mono-cal text-xs" data-testid="rate-loading">[LOADING…]</div>;
  }
  if (error) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="rate-error">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[LINK EXPIRED]</div>
        <h1 className="font-display font-bold text-3xl mb-4">We couldn't open this link</h1>
        <p className="text-[#666] mb-6">{error}</p>
        <Link to="/" className="cal-btn">Go home</Link>
      </div>
    );
  }

  if (done || data?.already_submitted) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="rate-done">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#00A36C] mb-2">[✓ FEEDBACK LOCKED]</div>
        <h1 className="font-display font-bold text-3xl mb-3">Thanks for your feedback</h1>
        <p className="text-[#666] mb-6">
          Your response for <strong>{data.cycle.employee_name}</strong> has been added to the vault anonymously. Once the minimum number of raters has responded, your manager will see the synthesised results — individual names are never shown.
        </p>
        <div className="cal-card p-4 bg-[#FAFAF5] text-left">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2 flex items-center gap-1"><Lock size={10}/> [ ANONYMITY GUARANTEE ]</div>
          <ul className="text-sm space-y-1 text-[#333]">
            <li>• Your name is NEVER shown next to any score or comment</li>
            <li>• The vault only unlocks after at least 3 peers have responded</li>
            <li>• An AI synthesiser aggregates comments into themes — no verbatim quotes are shared without aggregation</li>
          </ul>
        </div>
      </div>
    );
  }

  if (data?.cycle_closed) {
    return (
      <div className="max-w-xl mx-auto px-6 py-24 text-center" data-testid="rate-closed">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#FF4F00] mb-2">[REVIEW CLOSED]</div>
        <h1 className="font-display font-bold text-3xl mb-4">This review is no longer accepting feedback</h1>
        <p className="text-[#666] mb-6">The manager has closed or cancelled the review window. Thanks for being willing to contribute.</p>
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-6 py-12" data-testid="rate-page">
      <div className="font-mono-cal text-[11px] uppercase tracking-[0.2em] text-[#FF4F00] mb-3">
        [ 360° REVIEW · YOUR FEEDBACK ] · {ROLE_LABEL[data.rater.role_type]}
      </div>
      <h1 className="font-display font-bold text-4xl mb-2">
        Rate {data.cycle.employee_name}
      </h1>
      <p className="text-[#333] mb-5">
        <strong>{data.cycle.manager_name}</strong> is running this review covering {data.cycle.period_label?.toLowerCase() || "the review period"}. Your identity will never be shown next to any score or comment.
      </p>

      <div className="cal-card p-4 mb-6 bg-[#FAFAF5]" data-testid="rate-rubric">
        <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-2 flex items-center gap-1"><Lock size={10}/> [ ANONYMITY + RUBRIC ]</div>
        <div className="text-sm text-[#333] mb-2">
          Rate each task on a 1-5 scale. If you haven't observed enough to form an opinion, leave the score blank — a thoughtful "no opinion" is more valuable than a guess.
        </div>
        <div className="grid grid-cols-5 gap-2 text-[11px] font-mono-cal text-[#666]">
          {[1,2,3,4,5].map((s) => (
            <div key={s} className="border border-[#111]/10 p-2">
              <div className="font-display font-bold text-sm text-[#111]">{s}</div>
              <div>{SCORE_LABELS[s]}</div>
            </div>
          ))}
        </div>
      </div>

      <form onSubmit={submit} className="space-y-4" data-testid="rate-form">
        {tasks.map((t, i) => {
          const r = ratings[t.id] || {};
          return (
            <div key={t.id} className="cal-card p-4" data-testid={`rate-task-${t.id}`}>
              <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-1">[ TASK {i + 1} ]</div>
              <div className="font-display font-bold text-base mb-3">{t.description}</div>
              <div className="flex items-center gap-2 flex-wrap mb-3">
                {[1,2,3,4,5].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setScore(t.id, r.score === s ? null : s)}
                    className={`w-10 h-10 border border-[#111] font-display font-bold text-sm transition ${r.score === s ? "bg-[#FF4F00] text-white" : "bg-white hover:bg-[#F4F4F0]"}`}
                    data-testid={`rate-score-${t.id}-${s}`}
                    title={SCORE_LABELS[s]}
                  >{s}</button>
                ))}
                <button
                  type="button"
                  onClick={() => setScore(t.id, null)}
                  className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] underline ml-2"
                  data-testid={`rate-score-${t.id}-none`}
                >No opinion</button>
              </div>
              <textarea
                rows={2}
                value={r.comment || ""}
                onChange={(e) => setComment(t.id, e.target.value)}
                placeholder="Add a specific example to back up your score (optional)…"
                className="cal-input text-sm"
                data-testid={`rate-comment-${t.id}`}
              />
            </div>
          );
        })}

        <div className="cal-card p-4" data-testid="rate-overall-block">
          <div className="font-mono-cal text-[10px] uppercase tracking-widest text-[#666] mb-3">[ OPEN-ENDED FEEDBACK ]</div>
          <div className="space-y-3">
            <div>
              <label className="cal-label">Key strengths</label>
              <textarea rows={2} value={strengths} onChange={(e) => setStrengths(e.target.value)}
                placeholder={`What does ${data.cycle.employee_name.split(" ")[0]} do best?`}
                className="cal-input text-sm" data-testid="rate-strengths" />
            </div>
            <div>
              <label className="cal-label">Growth areas</label>
              <textarea rows={2} value={growth} onChange={(e) => setGrowth(e.target.value)}
                placeholder="Where could they develop? Be specific and constructive."
                className="cal-input text-sm" data-testid="rate-growth" />
            </div>
            <div>
              <label className="cal-label">Anything else?</label>
              <textarea rows={2} value={overall} onChange={(e) => setOverall(e.target.value)}
                placeholder="Any context or caveats about your relationship or observations…"
                className="cal-input text-sm" data-testid="rate-overall" />
            </div>
          </div>
        </div>

        <div className="cal-card p-4 flex items-center justify-between sticky bottom-4 bg-white" data-testid="rate-submit-bar">
          <div className="text-sm">
            <strong>{ratedCount}</strong> of {tasks.length} tasks rated
            {(strengths.trim() || growth.trim() || overall.trim()) && <span className="text-[#666] ml-2">· open feedback added</span>}
          </div>
          <button type="submit" disabled={submitting} className="cal-btn" data-testid="rate-submit">
            {submitting ? "Locking in vault…" : <><Users size={14}/> Submit anonymously →</>}
          </button>
        </div>
      </form>
    </div>
  );
}
